import type { Locales } from '@previo/core'
import CodebookValueNotFoundError from '../CodebookValueNotFoundError'
import { $t } from '../../composables/i18n'
import { CountryISO3 } from '../country/CountryISO3'
import { Codebook } from '../Codebook'

export enum LanguageId {
  CS = 1,
  EN = 2,
  DE = 3,
  FR = 4,
  IT = 5,
  SK = 6,
  ES = 7,
  RU = 8,
  SV = 9,
  NL = 10,
  PL = 11,
  JA = 12,
  ZH = 13,
  HU = 14,
  DA = 15,
  UK = 16,
  LT = 17,
  HR = 18,
  EL = 19,
  RO = 20,
  SL = 21,
  PT = 22,
  BG = 23,
}

export enum LanguageShortcut {
  CS = 'cs',
  EN = 'en',
  DE = 'de',
  FR = 'fr',
  IT = 'it',
  SK = 'sk',
  ES = 'es',
  RU = 'ru',
  SV = 'sv',
  NL = 'nl',
  PL = 'pl',
  JA = 'ja',
  ZH = 'zh',
  HU = 'hu',
  DA = 'da',
  UK = 'uk',
  LT = 'lt',
  HR = 'hr',
  EL = 'el',
  RO = 'ro',
  SL = 'sl',
  PT = 'pt',
  BG = 'bg',
}

export class Language extends Codebook<LanguageId> {
  private constructor(
    id: LanguageId,
    public readonly name: () => string,
    public readonly nameNative: string,
    public readonly shortcut: LanguageShortcut,
    public readonly countryISO3: CountryISO3,
  ) {
    super(id)
  }

  static getByShortcut = (shortcut: LanguageShortcut): Language => {
    const value = Language.all().find((item) => item.shortcut === shortcut)

    if (value === undefined) {
      throw new CodebookValueNotFoundError(Language.name, shortcut)
    }

    return value
  }

  static getByLocale = (locale: Locales): Language => {
    return Language.getByShortcut(locale as string as LanguageShortcut)
  }

  static readonly CS = new Language(
    LanguageId.CS,
    () => $t('Czech'),
    'Čeština',
    LanguageShortcut.CS,
    CountryISO3.CZE,
  )

  static readonly EN = new Language(
    LanguageId.EN,
    () => $t('English'),
    'English',
    LanguageShortcut.EN,
    CountryISO3.GBR,
  )

  static readonly DE = new Language(
    LanguageId.DE,
    () => $t('German'),
    'Deutsch',
    LanguageShortcut.DE,
    CountryISO3.DEU,
  )

  static readonly FR = new Language(
    LanguageId.FR,
    () => $t('French'),
    'Français',
    LanguageShortcut.FR,
    CountryISO3.FRA,
  )

  static readonly IT = new Language(
    LanguageId.IT,
    () => $t('Italian'),
    'Italiano',
    LanguageShortcut.IT,
    CountryISO3.ITA,
  )

  static readonly SK = new Language(
    LanguageId.SK,
    () => $t('Slovak'),
    'Slovensky',
    LanguageShortcut.SK,
    CountryISO3.SVK,
  )

  static readonly ES = new Language(
    LanguageId.ES,
    () => $t('Spanish'),
    'Español',
    LanguageShortcut.ES,
    CountryISO3.ESP,
  )

  static readonly RU = new Language(
    LanguageId.RU,
    () => $t('Russian'),
    'Русский',
    LanguageShortcut.RU,
    CountryISO3.RUS,
  )
  static readonly SV = new Language(
    LanguageId.SV,
    () => $t('Swedish'),
    'Svenska',
    LanguageShortcut.SV,
    CountryISO3.SWE,
  )

  static readonly NL = new Language(
    LanguageId.NL,
    () => $t('Dutch'),
    'Nederlands',
    LanguageShortcut.NL,
    CountryISO3.NLD,
  )

  static readonly PL = new Language(
    LanguageId.PL,
    () => $t('Polish'),
    'Polski',
    LanguageShortcut.PL,
    CountryISO3.POL,
  )

  static readonly JA = new Language(
    LanguageId.JA,
    () => $t('Japanese'),
    '日本語',
    LanguageShortcut.JA,
    CountryISO3.JPN,
  )

  static readonly ZH = new Language(
    LanguageId.ZH,
    () => $t('Chinese'),
    '中文',
    LanguageShortcut.ZH,
    CountryISO3.CHN,
  )

  static readonly HU = new Language(
    LanguageId.HU,
    () => $t('Hungarian'),
    'Magyar',
    LanguageShortcut.HU,
    CountryISO3.HUN,
  )

  static readonly DA = new Language(
    LanguageId.DA,
    () => $t('Danish'),
    'Dansk',
    LanguageShortcut.DA,
    CountryISO3.DNK,
  )

  static readonly UK = new Language(
    LanguageId.UK,
    () => $t('Ukrainian'),
    'Українська',
    LanguageShortcut.UK,
    CountryISO3.UKR,
  )

  static readonly LT = new Language(
    LanguageId.LT,
    () => $t('Lithuanian'),
    'Lietuvių',
    LanguageShortcut.LT,
    CountryISO3.LTU,
  )

  static readonly HR = new Language(
    LanguageId.HR,
    () => $t('Croatian'),
    'Hrvatski',
    LanguageShortcut.HR,
    CountryISO3.HRV,
  )

  static readonly EL = new Language(
    LanguageId.EL,
    () => $t('Greek'),
    'Ελληνική',
    LanguageShortcut.EL,
    CountryISO3.GRC,
  )

  static readonly RO = new Language(
    LanguageId.RO,
    () => $t('Romanian'),
    'Română',
    LanguageShortcut.RO,
    CountryISO3.ROU,
  )

  static readonly SL = new Language(
    LanguageId.SL,
    () => $t('Slovenian'),
    'Slovenski',
    LanguageShortcut.SL,
    CountryISO3.SVN,
  )

  static readonly PT = new Language(
    LanguageId.PT,
    () => $t('Portuguese'),
    'Português',
    LanguageShortcut.PT,
    CountryISO3.PRT,
  )

  static readonly BG = new Language(
    LanguageId.BG,
    () => $t('Bulgarian'),
    'български',
    LanguageShortcut.BG,
    CountryISO3.BGR,
  )
}
